import { CompleteAttachment, PendingAttachment, AttachmentAdapter } from '@assistant-ui/react';
import * as XLSX from 'xlsx';

export class SimpleTextAttachmentAdapter implements AttachmentAdapter {
    public accept = 'text/plain,text/html,text/markdown,text/csv,text/xml,text/json,text/css';

    public async add(state: { file: File }): Promise<PendingAttachment> {
        return {
            id: state.file.name,
            type: 'document',
            name: state.file.name,
            contentType: state.file.type,
            file: state.file,
            status: { type: 'requires-action', reason: 'composer-send' },
        };
    }

    public async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
        return {
            ...attachment,
            status: { type: 'complete' },
            content: [
                {
                    type: 'text',
                    text: `<attachment name=${attachment.name}>\n${await getFileText(attachment.file)}\n</attachment>`,
                },
            ],
        };
    }

    public async remove() {
        // noop
    }
}

export class XlsxAttachmentAdapter implements AttachmentAdapter {
    public accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

    public async add(state: { file: File }): Promise<PendingAttachment> {
        const csvContent = await convertXlsxToCsv(state.file);
        console.log('🚀 ~ XlsxAttachmentAdapter ~ add ~ csvContent:', csvContent);

        return {
            id: state.file.name,
            type: 'document',
            name: state.file.name,
            contentType: state.file.type,
            content: [
                {
                    type: 'text',
                    text: `<attachment name=${state.file.name}>\n${csvContent}\n</attachment>`,
                },
            ],
            file: new File([csvContent], state.file.name, { type: 'text/csv' }),
            status: { type: 'requires-action', reason: 'composer-send' },
        };
    }

    public async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
        const csvContent = await convertXlsxToCsv(attachment.file);
        console.log('🚀 ~ XlsxAttachmentAdapter ~ send ~ csvContent:', csvContent);

        return {
            ...attachment,
            status: { type: 'complete' },
            content: [
                {
                    type: 'text',
                    text: `<attachment name=${attachment.name}>\n${csvContent.toString()}\n</attachment>`,
                },
            ],
        };
    }

    public async remove() {
        // noop
    }
}

const getFileText = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);

        reader.readAsText(file);
    });

const convertXlsxToCsv = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });

                // Get the first worksheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                // Convert to CSV
                const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
                resolve(csvOutput);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => reject(error);

        reader.readAsArrayBuffer(file);
    });
